import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { useStore } from "./StoreContext"; // ✅ Import StoreContext

const CartContext = createContext();
export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const { products, fetchProducts } = useStore(); // ✅ Get store products
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem("cart");
    return savedCart ? JSON.parse(savedCart) : [];
  });

  const alertedProductsRef = useRef(new Set()); // ✅ Use a Ref to prevent duplicate alerts

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);

  
  const validateCart = useCallback(() => {
    if (products.length === 0) return;
  
    setCart((prevCart) =>
      prevCart.filter((item) => {
        const product = products.find((p) => p.id === item.id);
  
        if (!product) {
          console.warn("🚨 Removing item (not found in store):", item);
          return false;
        }
  
        if (product.stock <= 0) {
          console.warn("🚨 Removing item (out of stock):", item);
          return false;
        }
  
        return true;
      })
    );
  }, [products, setCart]); // ✅ Only runs when `products` changes
  
  // ✅ Fetch products on mount
  useEffect(() => {
    fetchProducts(); // ✅ Now fetchProducts is stable
  }, [fetchProducts]); // ✅ No infinite loop
  
  
  // ✅ Validate cart AFTER `products` updates
  useEffect(() => {
    if (products.length > 0) {
      validateCart();
    }
  }, [products, validateCart]); // ✅ Now safe to include validateCart
  
  
  
  

  const addToCart = async (product) => {
    await fetchProducts();
    // ✅ Check if product is out of stock
    if (product.stock <= 0) {
      alert(`❌ '${product.name}' is out of stock and cannot be added to the cart.`);
      return; // Prevent adding
    }
  
    setCart((prevCart) => {
      const existingItem = prevCart.find((item) => item.id === product.id);
  
      if (existingItem) {
        // ✅ Prevent exceeding stock
        if (existingItem.quantity >= product.stock) {
          if (!alertedProductsRef.current.has(product.id)) {
            alert(`⚠️ Only ${product.stock} left in stock!`);
            alertedProductsRef.current.add(product.id);
          }
          return prevCart;
        }
  
        return prevCart.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
  
      return [...prevCart, { ...product, quantity: 1 }];
    });
  };
  

  const removeFromCart = (productId) => {
    setCart((prevCart) => prevCart.filter((item) => item.id !== productId));
    alertedProductsRef.current.delete(productId); // ✅ Remove from alert tracking
  };

  const updateQuantity = (productId, quantity) => {
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.id === productId
          ? { ...item, quantity: Math.min(quantity, products.find(p => p.id === productId)?.stock || quantity) }
          : item
      )
    );
  };

  const clearCart = useCallback(() => {
    setCart([]);
    alertedProductsRef.current.clear();
    localStorage.removeItem("cart");
  }, [setCart]); // ✅ Now `clearCart` is stable

  return (
    <CartContext.Provider value={{ cart, addToCart, removeFromCart, updateQuantity, clearCart, validateCart }}>
      {children}
    </CartContext.Provider>
  );
};

export default CartProvider;
